import React, { useState, useEffect } from 'react'
import styles from './FileListView.module.css'
import { formatFileSize } from 'utils/general'
import CustomModal from 'components/CustomModal/CustomModal'
import { Modal } from 'react-bootstrap'
import { isHtmlFile } from 'helpers/file-type'

const FileListView = ({
  files,
  onDelete,
  onReplace,
  onSetAsIndex,
  indexFile,
  maxVisibleFiles = 3,
  className = '',
  isGithub,
  hasSingleFile = false,
  isMobile = false
}) => {
  const [showAllFiles, setShowAllFiles] = useState(false)
  const [visibleFiles, setVisibleFiles] = useState([])
  const [hiddenFiles, setHiddenFiles] = useState([])

  useEffect(() => {
    if (!files || files.length === 0) {
      setVisibleFiles([])
      setHiddenFiles([])
      return
    }

    if (files.length <= maxVisibleFiles || showAllFiles) {
      setVisibleFiles(files)
      setHiddenFiles([])
    } else {
      setVisibleFiles(files.slice(0, maxVisibleFiles))
      setHiddenFiles(files.slice(maxVisibleFiles))
    }
  }, [files, maxVisibleFiles, showAllFiles])

  return (
    <>
      {/* styles for the Modal - specifically targeted to our modal-content classes only */}
      <style>
        {`
            .file-list-modal-dialog {
              margin: 1.75rem auto;
              justify-content: center;
            }
            
            .file-list-modal-content {
              border-radius: 16px;
              overflow: hidden;
              width: 80%;
            }
          `}
      </style>

      <div className={styles.filesContainer}>
        {visibleFiles.map((file, index) => (
          <FileItem
            key={index}
            file={file}
            onDelete={onDelete}
            onReplace={onReplace}
            onSetAsIndex={onSetAsIndex}
            indexFile={indexFile}
            className={className}
            isGithub={isGithub}
            hasSingleFile={hasSingleFile}
            isFirstFile={index === 0}
            isMobile={isMobile}
          />
        ))}

        {hiddenFiles.length > 0 && (
          <div
            className={styles.moreFilesIndicator}
            onClick={() => setShowAllFiles(true)}
            role="button"
            tabIndex={0}
            aria-label={`Show all ${files.length} files`}
          >
            {/* Background blurred file to show behind the overlay */}
            <div className={styles.blurredFileBackground}>
              <div className={styles.fileIcon}>
                <img src={`/assets/icons/${hiddenFiles[0].icon || 'file-x.svg'}`} alt="File" height="36" />
              </div>
              <div className={styles.fileDetails}>
                <span className={styles.fileNameContainer}>
                  <div className={styles.fileName}>
                    <span className={styles.truncateText}>
                      {hiddenFiles[0].name || hiddenFiles[0].path || hiddenFiles[0].fileName}
                    </span>
                  </div>
                </span>
              </div>
              <div className={styles.fileSize}>{formatFileSize(hiddenFiles[0].size)}</div>
            </div>
            <div className={styles.moreFilesOverlay}>
              <div className={styles.moreFilesContent}>
                <span className={styles.moreFilesCount}>+{hiddenFiles.length}</span>
                <span className={styles.moreFilesText}>files</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <CustomModal
        show={showAllFiles}
        onHide={() => setShowAllFiles(false)}
        centered
        dialogClassName="file-list-modal-dialog"
        contentClassName="file-list-modal-content"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Project Files</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.filesList}>
            {files.map((file, index) => (
              <FileItem
                key={index}
                file={file}
                onDelete={onDelete}
                onSetAsIndex={onSetAsIndex}
                indexFile={indexFile}
                listView={true}
              />
            ))}
          </div>
        </Modal.Body>
      </CustomModal>
    </>
  )
}

const FileItem = ({
  file,
  onDelete,
  onReplace,
  onSetAsIndex,
  indexFile,
  className = '',
  listView = false,
  isGithub,
  hasSingleFile = false,
  isFirstFile = false,
  isMobile = false
}) => {
  if (!file) return null

  const icon = file.icon || 'pdf-file.png'
  const fileName = file.path || file.name || file.fileName
  const isCurrentIndex = fileName === indexFile
  const isHtml = isHtmlFile(fileName)
  const text = fileName + (isCurrentIndex ? ' (index)' : '')
  const fileSize = typeof file.size === 'number' ? file.size : file.size

  if (listView) {
    return (
      <div className={styles.fileListItem}>
        <div className={styles.fileListIcon}></div>
        <div className={styles.fileListName}>
          <span className={styles.truncateText}>{text}</span>
        </div>
        {!!fileSize && <div className={styles.fileListSize}>{formatFileSize(fileSize)}</div>}
        {isHtml && isCurrentIndex && (
          <div className={styles.indexBadgeContainer} title="This is your site's homepage">
            <img src="/assets/icons/check.svg" alt="homepage" className={styles.indexBadgeIcon} />
            <span className={styles.indexBadge}>Homepage</span>
          </div>
        )}
        {isHtml && !isCurrentIndex && onSetAsIndex && (
          <button
            type="button"
            className={styles.setAsIndexBtn}
            onClick={(e) => {
              e.preventDefault()
              onSetAsIndex(fileName)
            }}
            aria-label={`Set ${fileName} as homepage`}
          >
            Set as Homepage
          </button>
        )}
        <button
          type="button"
          className={styles.deleteListFile}
          onClick={(e) => {
            e.preventDefault()
            onDelete && onDelete(file)
          }}
          aria-label={`Delete file ${fileName}`}
        >
          <img height="24" width="24" src="/assets/icons/trash.svg" alt="delete" />
        </button>
      </div>
    )
  }

  const shouldShowDeleteIcon = !hasSingleFile || !isFirstFile
  const shouldApplyBlur = hasSingleFile && isFirstFile && !isMobile && !isGithub

  const handleFileAction = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (isGithub) return
    onDelete && onDelete(file)
  }

  return (
    <div className={`${styles.fileContainer} ${className} ${shouldApplyBlur ? styles.singleFileBlur : ''}`}>
      {shouldShowDeleteIcon && (
        <div className={styles.deleteContainer}>
          <button
            type="button"
            data-testid="FileItem-delete-button"
            className={styles.deleteFile}
            onClick={handleFileAction}
            aria-label={`Delete file ${fileName}`}
            disabled={isGithub}
            style={{ cursor: isGithub ? 'not-allowed' : 'pointer' }}
          >
            <img src="/assets/icons/trash.svg" height="24" width="24" alt="delete" />
          </button>
        </div>
      )}

      {shouldApplyBlur && (
        <button
          type="button"
          className={styles.replaceFileOverlay}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isGithub) return
            onReplace && onReplace(file)
          }}
          data-testid="FileItem-button-replace-file"
          aria-label={`Replace file ${fileName}`}
        >
          <span className={styles.replaceFileText}>
            <img src="/assets/icons/upload.png" className="me-3" alt="replace" height="20" width="20" />
            Replace file
          </span>
        </button>
      )}

      <div className={styles.fileItem}>
        <div className={styles.fileIcon}>
          <img src={`/assets/icons/${icon}`} alt={file.type || 'File'} height="36" />
        </div>
        <div className={styles.fileDetails}>
          <div className={styles.fileNameContainer}>
            <div className={styles.fileName} title={text}>
              <span className={styles.truncateText}>{text}</span>
            </div>
          </div>
        </div>
        {!!fileSize && <div className={styles.fileSize}>{formatFileSize(fileSize)}</div>}
        {isHtml && isCurrentIndex && (
          <div className={styles.indexBadgeContainer} title="This is your site's homepage">
            <img src="/assets/icons/check.svg" alt="homepage" className={styles.indexBadgeIcon} />
            <span className={styles.indexBadge}>Homepage</span>
          </div>
        )}
        {isHtml && !isCurrentIndex && onSetAsIndex && (
          <button
            type="button"
            className={styles.setAsIndexBtn}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onSetAsIndex(fileName)
            }}
            aria-label={`Set ${fileName} as homepage`}
          >
            Set as Homepage
          </button>
        )}
      </div>
    </div>
  )
}

export default FileListView
